import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid-premium';

function read(key: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(`colw:${key}`);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => typeof v === 'number' && v > 0 && v < 2000),
    ) as Record<string, number>;
  } catch {
    return {};
  }
}

/** localStorage, not the profile: one account, and a monitor width is wrong on a laptop. */
export function useColumnWidths(key: string, columns: GridColDef[]) {
  // State, not a read-once memo: hiding or reordering a column rebuilds `columns`, and a
  // snapshot taken at mount would re-apply the declared width over a resize done since.
  const [stored, setStored] = useState<Record<string, number>>(() => read(key));
  useEffect(() => setStored(read(key)), [key]);

  const sized = useMemo(
    () => columns.map((c) => (stored[c.field] ? { ...c, width: stored[c.field], flex: undefined } : c)),
    [columns, stored],
  );

  const remember = useCallback((field: string, width: number) => {
    setStored((prev) => {
      const next = { ...prev, [field]: Math.round(width) };
      try {
        localStorage.setItem(`colw:${key}`, JSON.stringify(next));
      } catch { /* not remembered across reloads */ }
      return next;
    });
  }, [key]);

  return { columns: sized, remember };
}
